import { NgClass, NgFor, NgIf } from '@angular/common';
import { ChangeDetectorRef, Component, EventEmitter, Input, Output, inject } from '@angular/core';

import { EpcotConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { ExternalPriceFormatterPipe } from '@frontend/sports/odds/feature/native-price';
import { NOT_APPLICABLE, TrackingService, trackingConstants } from '@frontend/sports/tracking/feature';
import { OptionPanelThreshold } from '@frontend/sports/types/configuration';
import { IconCustomComponent } from '@frontend/vanilla/shared/icons';
import { ceil } from 'lodash-es';

import { ShowInEpcotDirective } from '../common/epcot-config.directive';
import { CrmPromotionProviderService } from '../crm-promotion/crm-promotion-provider.service';
import { HorsesFixtureParticipantHelper } from '../horse-racing-shared/services/horses-fixture-participant-helper.service';
import { HorsesFixturePricingHelper } from '../horse-racing-shared/services/horses-fixture-pricing-helper.service';
import { HorsesFixtureSortingHelper } from '../horse-racing-shared/services/horses-fixture-sorting-helper.service';
import { FixturePickComponent } from '../option-pick/fixture-pick.component';
import { BaseLinkedGroupComponent } from './base-linked-group.component';
import { ParticipantOptionRendererDirective } from './participant-option-renderer.directive';

@Component({
    selector: 'ms-linked-option-group',
    templateUrl: './linked-option-group.component.html',
    standalone: true,
    imports: [
        ParticipantOptionRendererDirective,
        NgFor,
        NgClass,
        FixturePickComponent,
        IconCustomComponent,
        NgIf,
        ShowInEpcotDirective,
        ExternalPriceFormatterPipe,
    ],
})
export class LinkedOptionGroupComponent extends BaseLinkedGroupComponent {
    @Input() thresholds?: OptionPanelThreshold[];
    @Output() expandClicked = new EventEmitter<boolean>();

    threshold?: number;
    more = false;
    isEpcotEnabled: boolean = inject(EpcotConfig).isEnabled;

    constructor(
        public override sitecore: Sitecore,
        protected override horsesFixturePricingHelper: HorsesFixturePricingHelper,
        protected override horsesFixtureParticipantHelper: HorsesFixtureParticipantHelper,
        protected override horsesFixtureSortingHelper: HorsesFixtureSortingHelper,
        protected override crmPromotionProvider: CrmPromotionProviderService,
        private changeDetectorRef: ChangeDetectorRef,
        private trackingService: TrackingService,
    ) {
        super(sitecore, horsesFixturePricingHelper, horsesFixtureParticipantHelper, horsesFixtureSortingHelper, crmPromotionProvider);
    }

    showOption(index: number): boolean {
        return this.more || !this.threshold || index < this.threshold;
    }

    expand(): void {
        this.more = !this.more;
        this.expandClicked.emit(this.more);

        this.changeDetectorRef.detectChanges();

        this.trackingService.track(trackingConstants.EVENT_CLICKS, {
            [trackingConstants.COMPONENT_CATEGORY_EVENT]: 'Competition Page Interactions',
            [trackingConstants.COMPONENT_LABEL_EVENT]: 'Visible Selections',
            [trackingConstants.COMPONENT_ACTION_EVENT]: this.more ? 'Show More' : 'Show Less',
            [trackingConstants.COMPONENT_POSITION_EVENT]: NOT_APPLICABLE,
            [trackingConstants.COMPONENT_LOCATION_EVENT]: NOT_APPLICABLE,
        });
    }

    rendererChange(columns: number): void {
        if (this.thresholds) {
            let threshold = 6;

            const active = this.thresholds
                .filter((current) => current.columns <= columns)
                .sort((first, second) => first.columns - second.columns)
                .pop();

            if (active) {
                threshold = active.threshold;
            }

            threshold = ceil(threshold / columns) * columns;

            if (this.threshold !== threshold) {
                this.threshold = threshold;
                this.changeDetectorRef.detectChanges();
            }
        }
    }
}
